"use client";

import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Loader2, Send, ShieldAlert, Zap } from "lucide-react";
import { toast } from "sonner";
import type { TechficientQuoteResultCardData } from "../../types/insurabot-cards";
import { techficientService } from "../../services/techficientService";
import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";
import { CarrierLogo } from "@/app/_module/components/CarrierLogo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { ManageProfile } from "@/app/(authenticated)/quote-enroll/_modules/components/ManageProfile/ManageProfile";

type ApplyArgs = {
  qrrId: string;
  supportsAppSubmit: boolean | undefined;
  submittableType?: "full_eapp" | "drop_ticket" | "quote_only";
};

// Apply bounces with "Agent eligibility check failed: <Code>" when the agent's
// producer profile is short something the carrier requires. Those are fixable
// in Manage profile, so route the agent there instead of printing the code.
function profileIssueFrom(msg: string): { kind: "license" } | { kind: "profile"; detail: string } | null {
  if (/StateLicense/i.test(msg)) return { kind: "license" };
  const code = /eligibility check failed:\s*([A-Za-z]+)/i.exec(msg)?.[1];
  if (code && /^Profile/i.test(code)) {
    return {
      kind: "profile",
      detail: code.replace(/^Profile/i, "").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase(),
    };
  }
  return null;
}

const S =
  "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

export default function TechficientQuoteResultCard({
  data,
}: {
  data: TechficientQuoteResultCardData;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Lead-less Apply pauses here for consent: the quote may be for a
  // family member of an existing client — never silently add people to
  // the CRM. Confirming re-enters handleApply with confirmed: true (the
  // confirm click also supplies the user gesture drop-ticket popups need).
  const [pendingApply, setPendingApply] = useState<ApplyArgs | null>(null);
  const floater = useEnrollmentFloater();

  // ── Producer-profile gaps (missing state licence etc.) ──
  // Same query key as the Quote & Enroll page, so this is usually a cache hit.
  const { data: eligibility } = useQuery({
    queryKey: ["techficient", "eligibility"],
    queryFn: () => techficientService.getEligibility(),
    staleTime: 60_000,
  });
  const clientState = data.state?.trim().toUpperCase() || undefined;
  const licensedStates = new Set(
    (eligibility?.stateLicenses || []).map((l) => l.stateCode?.toUpperCase()),
  );
  const missingLicense =
    !!eligibility?.provisioned && !!clientState && !licensedStates.has(clientState);
  const [profileIssue, setProfileIssue] = useState<
    { kind: "license"; state?: string } | { kind: "profile"; detail: string } | null
  >(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  // The Apply that bounced — "Apply again" replays it after the fix.
  const [retry, setRetry] = useState<ApplyArgs | null>(null);
  // Closed the profile without saving → stop gating Apply locally and let
  // the carrier decide (our licence list could lag theirs).
  const gateDismissed = useRef(false);

  const openProfileFor = (
    issue: NonNullable<typeof profileIssue>,
    attempt: ApplyArgs,
  ) => {
    setRetry(attempt);
    setProfileIssue(issue);
    setProfileSaved(false);
    setError(null);
    setProfileOpen(true);
  };

  // Zero carriers is a real outcome (health/coverage/product knocks them all
  // out) — show a clear "no matches" state so it never looks like a silent
  // failure in the chat.
  if (!data?.results || data.results.length === 0) {
    return (
      <div
        className="mt-3 w-full min-w-0 max-w-full rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500"
        style={{ boxShadow: S }}
      >
        <div className="bg-amber-50 border border-amber-200 px-4 py-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              No carriers returned a quote
            </p>
            <p className="text-amber-800/80 mt-1 leading-relaxed">
              The coverage amount, product type, or health details fall outside
              what your appointed carriers offer for this applicant. Try a lower
              coverage amount, a different product, or adjusting the health
              answers, then quote again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const toNum = (v: any): number | undefined => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const normalized = data.results.map((r) => ({
    ...r,
    monthlyPremium: toNum(r.monthlyPremium),
    annualPremium: toNum(r.annualPremium),
  }));
  const sorted = [...normalized].sort(
    (a, b) => (a.monthlyPremium ?? Infinity) - (b.monthlyPremium ?? Infinity),
  );
  const cheapestId = sorted[0]?.quoteRequestResultId;

  const handleApply = async (
    qrrId: string,
    supportsAppSubmit: boolean | undefined,
    submittableType?: "full_eapp" | "drop_ticket" | "quote_only",
    opts?: { confirmed?: boolean },
  ) => {
    if (!supportsAppSubmit) {
      setError("This product can't be submitted via eApp yet.");
      return;
    }
    // Known licence gap → fix it first rather than burning a doomed Apply
    // (which would also create the lead and push the client).
    if (missingLicense && !gateDismissed.current) {
      openProfileFor(
        { kind: "license", state: clientState },
        { qrrId, supportsAppSubmit, submittableType },
      );
      return;
    }
    // No lead attached → pause for explicit consent before anything is
    // created in the CRM (and prompt a DOB/state double-check — they drive
    // both the rates and the application).
    if (!data.leadId && !opts?.confirmed) {
      setPendingApply({ qrrId, supportsAppSubmit, submittableType });
      setError(null);
      return;
    }
    const picked = data.results.find((r) => r.quoteRequestResultId === qrrId);
    // Any submittable product is eligible for the direct Submission API:
    // drop tickets AND full eApps that support submit-on-behalf (e.g. Banner).
    // We try the API first so the app is filed programmatically instead of
    // pushing the agent through the hosted "agency submission" screens. If the
    // carrier's question set wants more than we hold — or Techficient's
    // submit/question-set endpoint errors for that carrier (UHL currently
    // 500s on their side) — we fall back to the hosted eApp. Never dead-end.
    const canDirectSubmit =
      submittableType === "drop_ticket" || submittableType === "full_eapp";

    // Reserve the popup window NOW, synchronously, while the click gesture is
    // live — the popup blocker kills window.open calls made after an await.
    // It shows a "Preparing…" screen until the backend returns the carrier's
    // signature URL (both drop-ticket and Banner finish pages block framing).
    let pendingPopup = false;
    if (canDirectSubmit) {
      pendingPopup = floater.openPendingPopup();
      // Blocked → we can still fall back to the hosted-iframe flow below.
    }

    setBusy(qrrId);
    setError(null);
    try {
      // Lead-less quick quote? The backend mints a CRM lead from the quote's
      // own stored inputs (name/DOB/state/build) — an application always
      // needs a client record, so we create it invisibly instead of
      // dead-ending the agent with "pick a lead first".
      let effectiveLeadId = data.leadId;
      if (!effectiveLeadId) {
        const ensured = await techficientService.ensureQuoteLead(
          data.quoteRequestId,
        );
        effectiveLeadId = ensured.leadId;
        data.leadId = ensured.leadId; // subsequent Applies on this card reuse it
      }
      const link = await techficientService.pushClient(effectiveLeadId, {});

      if (canDirectSubmit && pendingPopup) {
        // Fully-within path: backend answers the carrier's question set
        // from the lead + quote and submits directly — Techficient's
        // hosted UI (and its tab-spawning handoff buttons) never renders.
        // Wrapped so a carrier-side 500 (UHL) doesn't error the agent out —
        // it quietly falls through to the hosted eApp launch instead.
        try {
          const res = await techficientService.directSubmitEapp({
            leadId: effectiveLeadId,
            techficientQuoteRequestId: data.quoteRequestId,
            techficientQuoteRequestResultId: qrrId,
          });
          if (res.submitted && res.launchUrl) {
            floater.completePendingPopup({
              caseId: res.caseId || undefined,
              launchUrl: res.launchUrl,
              carrier: res.carrier || picked?.carrier,
              product: res.product || picked?.product,
              leadId: effectiveLeadId,
              launchMode: "popup",
            });
            return;
          }
          if (res.submitted && !res.launchUrl) {
            // Fully submitted with no carrier finish step — nothing to show.
            floater.cancelPendingPopup();
            toast.success("Application submitted", {
              description: `${picked?.carrier || "Carrier"} accepted the application — no signature step required.`,
            });
            return;
          }
          // needsInput → this product's question set wants more than we hold.
          // Fall through to the hosted eApp (iframe) so the agent can answer.
          floater.cancelPendingPopup();
        } catch {
          // Submission API errored for this carrier (e.g. Techficient 500 on
          // getApplicationQuestionSet / submitapplication for UHL). Fall back
          // to the hosted eApp launch below rather than surfacing an error.
          floater.cancelPendingPopup();
        }
      }

      const init = await techficientService.initiateEapp({
        leadId: effectiveLeadId,
        techficientClientId: link.techficientClientId,
        techficientQuoteRequestId: data.quoteRequestId,
        techficientQuoteRequestResultId: qrrId,
      });
      if ((init as any).resumed) {
        toast("Continuing your application", {
          description:
            "You already started this one — picking up where it left off, not starting over.",
        });
      }
      floater.launch({
        caseId: init.caseId,
        launchUrl: init.launchUrl,
        carrier: picked?.carrier,
        product: picked?.product,
      });
    } catch (e: any) {
      floater.cancelPendingPopup();
      const msg = String(e?.response?.data?.message || e?.message || "");
      const issue = profileIssueFrom(msg);
      if (issue) {
        openProfileFor(
          issue.kind === "license" ? { kind: "license", state: clientState } : issue,
          { qrrId, supportsAppSubmit, submittableType },
        );
      } else {
        setError(msg || "Failed to start eApp");
      }
    } finally {
      setBusy(null);
    }
  };

  // ─── Term / coverage chip — shows once at the top of the card so every
  // carrier tile doesn't have to repeat it. Drives the "year shows more
  // prominently" the agent asked for.
  const coverageLabel = (() => {
    const face = data.faceAmount
      ? data.faceAmount >= 1_000_000
        ? `$${(data.faceAmount / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`
        : `$${Math.round(data.faceAmount / 1000)}K`
      : null;
    const term =
      data.coverageType === "Term" && data.termYears
        ? `${data.termYears}-year term`
        : data.coverageType === "FinalExpense"
        ? "Final Expense"
        : null;
    return [face, term].filter(Boolean).join(" · ");
  })();

  return (
    <div
      className="mt-3 w-full min-w-0 max-w-full rounded-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 bg-gradient-to-br from-white via-white to-slate-50/40"
      style={{ boxShadow: S }}
    >
      <div className="px-4 pt-4 pb-3 min-w-0">
        {/* Header strip */}
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.14em]">
              Multi-carrier quote
            </p>
            {coverageLabel && (
              <p className="text-sm font-semibold text-gray-900 mt-0.5">
                {coverageLabel}
              </p>
            )}
          </div>
          <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {data.results.length}{" "}
            {data.results.length === 1 ? "carrier" : "carriers"}
          </span>
        </div>
        {data.applicantSummary && (
          <p className="text-[11px] text-gray-500 mb-3 truncate">
            {data.applicantSummary}
          </p>
        )}

        {/* Carrier tiles */}
        <div
          className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1"
          style={{ scrollbarWidth: "thin" }}
        >
          {sorted.map((r) => {
            const isBest = r.quoteRequestResultId === cheapestId;
            const isBusy = busy === r.quoteRequestResultId;
            const isEnrollable = !!r.supportsAppSubmit;
            return (
              <QuoteTile
                key={r.quoteRequestResultId}
                carrier={r.carrier}
                product={r.product}
                monthlyPremium={r.monthlyPremium}
                annualPremium={r.annualPremium}
                healthClass={r.healthClass}
                instant={r.instant}
                isBest={isBest}
                isBusy={isBusy}
                isEnrollable={isEnrollable}
                termYears={
                  data.coverageType === "Term" ? data.termYears ?? null : null
                }
                onApply={() =>
                  handleApply(
                    r.quoteRequestResultId,
                    isEnrollable,
                    r.submittableType,
                  )
                }
              />
            );
          })}
        </div>

        {/* Licence gap — warn before Apply, and stay up after a bounce
            with a one-click way into the profile. */}
        {!profileSaved &&
          (profileIssue ||
            (missingLicense && sorted.some((r) => r.supportsAppSubmit))) && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 flex items-start gap-2.5">
              <ShieldAlert className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-amber-900">
                  {profileIssue?.kind === "profile"
                    ? "Your producer profile needs an update"
                    : `No ${clientState ? `${clientState} ` : "state "}license on your profile`}
                </p>
                <p className="text-[11px] text-amber-800/80 mt-0.5 leading-relaxed">
                  {profileIssue?.kind === "profile"
                    ? `Carriers need more on your producer profile before you can apply (${profileIssue.detail}).`
                    : `Carriers won't take an application for ${
                        clientState ? `a ${clientState}` : "this"
                      } client until your license for that state is on file.`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setProfileIssue(
                      profileIssue || { kind: "license", state: clientState },
                    );
                    setProfileOpen(true);
                  }}
                  className="mt-2 text-[11px] font-semibold rounded-md px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700"
                >
                  {profileIssue?.kind === "profile"
                    ? "Open producer profile"
                    : `Add ${clientState ? `${clientState} ` : ""}license`}
                </button>
              </div>
            </div>
          )}
        {profileSaved && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 flex items-center gap-2.5">
            <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <p className="text-[12px] font-medium text-emerald-900 flex-1 min-w-0">
              {profileIssue?.kind === "license" && profileIssue.state
                ? `${profileIssue.state} license saved.`
                : "Profile saved."}
            </p>
            {retry && (
              <button
                type="button"
                onClick={() => {
                  const r = retry;
                  setRetry(null);
                  setProfileSaved(false);
                  setProfileIssue(null);
                  void handleApply(r.qrrId, r.supportsAppSubmit, r.submittableType);
                }}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 flex-shrink-0"
              >
                Apply again
              </button>
            )}
          </div>
        )}
        {pendingApply && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3">
            <p className="text-[12px] font-semibold text-blue-900">
              Create a lead for{" "}
              {data.applicantSummary?.split("·")[0]?.trim() || "this applicant"}
              ?
            </p>
            <p className="text-[11px] text-blue-800/80 mt-1 leading-relaxed">
              Applying needs a client record. We&apos;ll first check your leads
              for an existing match — no duplicates get created. If this quote
              is for a family member of an existing client, that&apos;s fine:
              they get their own lead.
            </p>
            <p className="text-[11px] font-medium text-amber-700 mt-1.5 flex items-start gap-1">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              Double-check the date of birth and state before continuing —
              they drive the rates and the application.
            </p>
            {data.applicantSummary && (
              <p className="text-[11px] text-blue-900/70 mt-1.5 font-medium">
                {data.applicantSummary}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2.5">
              <button
                type="button"
                onClick={() => {
                  const p = pendingApply;
                  setPendingApply(null);
                  void handleApply(p.qrrId, p.supportsAppSubmit, p.submittableType, {
                    confirmed: true,
                  });
                }}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700"
              >
                Create lead &amp; continue
              </button>
              <button
                type="button"
                onClick={() => setPendingApply(null)}
                className="text-[11px] font-medium rounded-md px-3 py-1.5 text-gray-600 hover:bg-white"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="mt-3 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </p>
        )}
      </div>

      <Dialog
        open={profileOpen}
        onOpenChange={(v) => {
          setProfileOpen(v);
          if (!v && !profileSaved) gateDismissed.current = true;
        }}
      >
        <DialogContent className="manage-profile-dialog sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage producer profile</DialogTitle>
          </DialogHeader>
          {eligibility ? (
            <ManageProfile
              eligibility={eligibility}
              focusStateCode={
                profileIssue?.kind === "license" ? profileIssue.state : undefined
              }
              onClose={() => setProfileOpen(false)}
              onSaved={() => {
                setProfileSaved(true);
                setProfileOpen(false);
                setError(null);
              }}
            />
          ) : (
            <div className="py-10 flex justify-center text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Individual carrier tile — designed to feel premium without being noisy.
// Hierarchy: brand → premium → term → action. Hover state lifts the card.
// ─────────────────────────────────────────────────────────────────────────

function QuoteTile({
  carrier,
  product,
  monthlyPremium,
  annualPremium,
  healthClass,
  instant,
  isBest,
  isBusy,
  isEnrollable,
  termYears,
  onApply,
}: {
  carrier: string;
  product: string;
  monthlyPremium: number | undefined;
  annualPremium: number | undefined;
  healthClass?: string;
  instant?: boolean;
  isBest: boolean;
  isBusy: boolean;
  isEnrollable: boolean;
  termYears: number | null;
  onApply: () => void;
}) {
  return (
    <div
      className={`flex-shrink-0 w-[220px] rounded-xl p-4 flex flex-col transition-all duration-200 hover:-translate-y-0.5 ${
        isBest
          ? "bg-gradient-to-br from-emerald-50 via-emerald-50/70 to-emerald-100/60 ring-2 ring-emerald-400/50"
          : "bg-white"
      }`}
      style={{
        boxShadow: isBest
          ? "rgba(16,185,129,0.25) 0px 0px 0px 1px, rgba(16,185,129,0.15) 0px 10px 28px -6px, rgba(16,185,129,0.08) 0px 2px 4px"
          : "rgba(0,0,0,0.05) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 4px 12px -2px, rgba(0,0,0,0.02) 0px 1px 2px",
      }}
    >
      {/* Best Value pill — sits at the top so the eye lands on it first.
          Solid emerald-600 fill so it reads as the tile's leading element
          when the whole box is washed green. */}
      {isBest && (
        <div className="flex items-center gap-1 mb-2.5 -mt-0.5">
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5 rounded shadow-sm shadow-emerald-600/30">
            <Zap className="h-2.5 w-2.5 fill-current" />
            Best Value
          </span>
        </div>
      )}

      {/* Brand row */}
      <div className="flex items-center gap-2 mb-3">
        <CarrierLogo name={carrier} size={32} />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-bold text-gray-900 truncate leading-tight">
            {carrier}
          </p>
          <p className="text-[10px] text-gray-500 truncate leading-tight mt-0.5">
            {product}
          </p>
        </div>
      </div>

      {/* Premium block — monthly is the hero, but the yearly number sits
          right under it with serious typographic weight so an agent can
          quote either price to a prospect without squinting. The two prices
          share a baseline grid so the dollar signs align visually. */}
      <div className="mb-3">
        <div className="flex items-baseline gap-1">
          <span
            className={`text-3xl font-black leading-none tabular-nums tracking-tight ${
              isBest ? "text-emerald-700" : "text-gray-900"
            }`}
            style={{ fontFeatureSettings: '"tnum"' }}
          >
            ${monthlyPremium?.toFixed(2) ?? "—"}
          </span>
          <span className="text-[11px] text-gray-400 font-medium">/mo</span>
        </div>
        {annualPremium != null && (
          <div className="mt-1.5 flex items-baseline gap-1">
            <span
              className={`text-base font-bold tabular-nums leading-none ${
                isBest ? "text-emerald-700/90" : "text-gray-700"
              }`}
              style={{ fontFeatureSettings: '"tnum"' }}
            >
              $
              {annualPremium.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="text-[10px] text-gray-400 font-medium">/yr</span>
          </div>
        )}
      </div>

      {/* Small term chip — the user wanted yearly premium prominent, not the
          term length. Keep the term as a quiet chip so the agent still knows
          what they're quoting, but don't compete with the price hierarchy.
          On the Best Value tile it picks up the green theme so the box reads
          as one unified green callout. */}
      <div className="mb-2">
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md ${
            isBest
              ? "text-emerald-700 bg-emerald-200/60 ring-1 ring-emerald-300/50"
              : "text-gray-700 bg-gray-100"
          }`}
        >
          {termYears ? `${termYears}-yr term` : "Whole life"}
        </span>
      </div>

      {/* Meta row — tight badge cluster, no quote-only here (the disabled
          CTA below already says "Quote only" so we don't duplicate). On the
          Best Value tile every badge folds to emerald so nothing breaks the
          green wash. */}
      {(instant || isEnrollable) && (
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {instant && (
            <Badge tone="emerald">
              <Zap className="h-2.5 w-2.5" />
              Instant
            </Badge>
          )}
          {isEnrollable && (
            <Badge tone={isBest ? "emerald" : "indigo"}>
              <Check className="h-2.5 w-2.5" />
              eApp
            </Badge>
          )}
        </div>
      )}

      {/* Spacer — pushes the CTA to the bottom edge of the tile so every
          tile's Apply button lines up across the row even when the badge /
          health-class slots have different heights. */}
      <div className="flex-grow" />

      {/* Health class — own row, left-aligned, subtle. Was floating right
          earlier which left an awkward gap when no badges were present. */}
      {healthClass && (
        <p
          className={`text-[10px] mb-2 truncate ${
            isBest ? "text-emerald-700/80" : "text-gray-500"
          }`}
          title={healthClass}
        >
          {healthClass}
        </p>
      )}

      {/* Apply CTA — full-bleed, premium feel. Uses an arrow icon (clean
          forward motion) instead of sparkles. Disabled state goes subtle
          gray so the eye skips it. */}
      <button
        type="button"
        disabled={isBusy || !isEnrollable}
        onClick={onApply}
        className={`group/btn relative w-full overflow-hidden rounded-lg py-2.5 px-3 text-[12px] font-semibold transition-all flex items-center justify-center gap-2 ${
          !isEnrollable
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : isBest
              ? "bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] shadow-sm shadow-emerald-600/30"
              : "bg-gray-900 text-white hover:bg-black active:scale-[0.98] shadow-sm shadow-gray-900/20"
        }`}
      >
        {/* Hover shimmer — pure CSS, no JS */}
        {isEnrollable && (
          <span
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full"
            aria-hidden
          />
        )}
        {isBusy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin relative" />
        ) : (
          <>
            {isEnrollable && (
              <Send className="h-3.5 w-3.5 relative transition-transform duration-200 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
            )}
            <span className="relative">
              {isEnrollable ? "Apply now" : "Quote only"}
            </span>
          </>
        )}
      </button>
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "emerald" | "indigo" | "gray";
}) {
  const palette =
    tone === "emerald"
      ? "text-emerald-700 bg-emerald-50 ring-emerald-100"
      : tone === "indigo"
      ? // Brand-bound — the "indigo" slot is the eApp badge, which should
        // pick up the white-label primary so MRFG / Direct130 / etc. each
        // get their own color rather than a generic indigo.
        "text-primary bg-primary/10 ring-primary/20"
      : "text-gray-600 bg-gray-100 ring-gray-100";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ring-inset ${palette}`}
    >
      {children}
    </span>
  );
}
